/**
 * VERONI cart cross-sell with a strict gemstone eligibility rule.
 * A tile is rendered only when its added variant exactly matches a Gemstone
 * option already present in the cart. No match means no recommendation block.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const cache = new Map();
const SHOW = 2;
const GEM_NAMES = new Set(['gemstone', 'colored stone', 'stone']);
const GOLD_NAMES = new Set(['gold color', 'color', 'metal']);
const normalize = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
const valuesFrom = (raw) => new Set(String(raw || '').split('||').map(normalize).filter(Boolean));

function optionIndex(product, names) {
  return (product.options || []).findIndex((option) => names.has(normalize(option.name || option)));
}

// Gold shown when the cart's gold is not available: the order the product
// cards pre-select (yellow, then white, then rose).
const GOLD_ORDER = ['yellow gold', 'white gold', 'rose gold'];

function matchingVariant(product, gemstones, goldColors) {
  const gemIndex = optionIndex(product, GEM_NAMES);
  if (gemIndex < 0 || !gemstones.size) return null;
  const goldIndex = optionIndex(product, GOLD_NAMES);
  let fallback = null;
  let fallbackRank = Infinity;

  for (const variant of product.variants || []) {
    if (!variant.available) continue;
    if (!gemstones.has(normalize(variant.options?.[gemIndex]))) continue;
    const gold = goldIndex >= 0 ? normalize(variant.options?.[goldIndex]) : '';
    if (gold && goldColors.has(gold)) return variant;
    const rank = GOLD_ORDER.includes(gold) ? GOLD_ORDER.indexOf(gold) : GOLD_ORDER.length;
    if (rank < fallbackRank) {
      fallback = variant;
      fallbackRank = rank;
    }
  }
  return fallback;
}

/** The variant's own photo (the white-background 01 shot in its gold); with
 *  none, a same-gold sibling's photo before the product's featured image. */
function variantImage(product, variant) {
  if (variant.featured_image?.src) return variant.featured_image.src;
  const goldIndex = optionIndex(product, GOLD_NAMES);
  if (goldIndex >= 0) {
    const gold = variant.options?.[goldIndex];
    const sibling = (product.variants || []).find((v) => v.featured_image?.src && v.options?.[goldIndex] === gold);
    if (sibling) return sibling.featured_image.src;
  }
  return product.featured_image;
}

class VeroniCartCrossSell extends HTMLElement {
  #controller = new AbortController();
  #renderedFor = '';
  #inFlight = false;

  connectedCallback() { this.hydrate(); }
  disconnectedCallback() { this.#controller.abort(); }

  async hydrate() {
    if (this.#inFlight) return;
    const productId = this.dataset.productId;
    const list = this.querySelector('[data-cross-list]');
    const gemstones = valuesFrom(this.dataset.gemstones);
    const goldColors = valuesFrom(this.dataset.goldColors);
    if (!productId || !list || !gemstones.size) {
      this.hidden = true;
      return;
    }

    const key = `${productId}|${this.dataset.inCart || ''}|${[...gemstones].sort()}|${[...goldColors].sort()}`;
    if (this.#renderedFor === key && list.children.length) return;
    this.#inFlight = true;
    this.#controller = new AbortController();
    const inCart = new Set((this.dataset.inCart || '').split(',').map((id) => id.trim()).filter(Boolean));

    try {
      const products = await this.#recommendations(productId);
      const picks = products
        .filter((product) => !inCart.has(String(product.id)))
        .map((product) => ({ product, variant: matchingVariant(product, gemstones, goldColors) }))
        .filter((pick) => pick.variant)
        .slice(0, SHOW);

      const currentList = this.querySelector('[data-cross-list]');
      if (!currentList) return;
      currentList.replaceChildren(...picks.map((pick) => this.#tile(pick.product, pick.variant)));
      this.hidden = picks.length === 0;
      this.#renderedFor = key;
    } catch {
      this.hidden = true;
    } finally {
      this.#inFlight = false;
    }
  }

  async #recommendations(productId) {
    if (cache.has(productId)) return cache.get(productId);
    const url = new URL('/recommendations/products.json', window.location.origin);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', '10');
    url.searchParams.set('intent', 'related');
    const response = await fetch(url, {
      signal: this.#controller.signal,
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`recommendations ${response.status}`);
    const data = await response.json();
    const products = (data.products || []).filter((product) => product.available);
    cache.set(productId, products);
    return products;
  }

  #tile(product, variant) {
    const gemIndex = optionIndex(product, GEM_NAMES);
    const gemstone = variant.options?.[gemIndex] || '';
    const productUrl = new URL(product.url, window.location.origin);
    productUrl.searchParams.set('variant', variant.id);

    const row = document.createElement('div');
    row.className = 'v-cross';
    const media = document.createElement('a');
    media.className = 'v-cross__media';
    media.href = productUrl.toString();
    const image = variantImage(product, variant);
    if (image) {
      const img = document.createElement('img');
      const src = new URL(image, window.location.origin);
      src.searchParams.set('width', '160');
      img.src = src.toString();
      img.alt = `${gemstone} — ${product.title}`;
      img.width = 80;
      img.height = 80;
      img.loading = 'lazy';
      img.decoding = 'async';
      media.append(img);
    }

    const body = document.createElement('a');
    body.className = 'v-cross__body';
    body.href = productUrl.toString();
    const name = document.createElement('b');
    name.textContent = String(product.title).split(' ')[0];
    const meta = document.createElement('span');
    meta.textContent = `${gemstone} · ${this.#money(variant.price)}`;
    body.append(name, meta);
    row.append(media, body);

    // Rings are made to size, chosen on the product page: an Add here put rings
    // in the bag with no size (audit 2026-10-20).
    if (normalize(product.type) === 'rings') {
      const choose = document.createElement('a');
      choose.className = 'v-btn-mini';
      choose.href = productUrl.toString();
      choose.textContent = 'Choose size';
      choose.setAttribute('aria-label', `Choose a size for ${product.title} in ${gemstone}`);
      row.append(choose);
      return row;
    }

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'v-btn-mini';
    button.textContent = 'Add';
    button.setAttribute('aria-label', `Add ${product.title} in ${gemstone}`);
    button.addEventListener('click', () => this.#add(button, variant.id), { signal: this.#controller.signal });
    row.append(button);
    return row;
  }

  async #add(button, variantId) {
    button.disabled = true;
    const original = button.textContent;
    button.textContent = 'Adding';
    const sections = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => element.dataset.sectionId)
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity: 1 }],
      promise: deferred.promise,
    }));

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: sections.join(',') }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || 'add failed');
      const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'veroni-cart-cross-sell',
          itemCount: 1,
          sections: result.sections,
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error instanceof Error ? error : new Error(String(error)));
      button.disabled = false;
      button.textContent = original;
    }
  }

  #money(cents) {
    const value = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: this.dataset.currency || 'USD',
        minimumFractionDigits: value % 1 === 0 ? 0 : 2,
      }).format(value);
    } catch { return `$${value.toFixed(2)}`; }
  }
}

if (!customElements.get('veroni-cart-cross-sell')) {
  customElements.define('veroni-cart-cross-sell', VeroniCartCrossSell);
}

function hydrateAll() {
  document.querySelectorAll('veroni-cart-cross-sell').forEach((element) => element.hydrate?.());
}
const observer = new MutationObserver(() => {
  if (document.querySelector('veroni-cart-cross-sell')) hydrateAll();
});
const start = () => {
  observer.observe(document.body, { childList: true, subtree: true });
  hydrateAll();
};
if (document.body) start();
else document.addEventListener('DOMContentLoaded', start, { once: true });
